import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Download,
  RefreshCw,
  CheckSquare,
  Edit3,
  Save,
  X,
  BarChart3,
  Users,
  TrendingUp,
  Award,
  Search,
  Eye,
  BookOpen,
  Activity,
} from 'lucide-react';
import { StatCard } from '@azyntor-labs/nexus-ui';
import { examsApi, gradebookApi, analyticsApi } from '@/api/endpoints';
import { Exam } from '@/types';
import { toast } from '@/utils/toast';

interface GradeEntry {
  attempt_id: string;
  student_id: string;
  student_name: string;
  student_email: string;
  score: number;
  total_marks: number;
  percentage: number;
  grade: string;
  passed: boolean;
  feedback: string;
  submitted_at: string;
  graded_at: string;
  plagiarism_penalty?: number;
}

interface GradeStats {
  total_attempts: number;
  graded_count: number;
  average_score: number;
  highest_score: number;
  lowest_score: number;
  pass_rate: number;
  median_score: number;
  std_deviation: number;
  grade_distribution: Record<string, number>;
}

export const GradebookPage = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [selectedExamId, setSelectedExamId] = useState('');
  const [page, setPage] = useState(1);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editScore, setEditScore] = useState(0);
  const [editFeedback, setEditFeedback] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkScore, setBulkScore] = useState(0);
  const [bulkFeedback, setBulkFeedback] = useState('');
  const [confirmRecalc, setConfirmRecalc] = useState(false);
  const [examSearch, setExamSearch] = useState('');
  const [examDropdownOpen, setExamDropdownOpen] = useState(false);

  // Fetch overall tenant analytics
  const { data: tenantRes } = useQuery({
    queryKey: ['tenant-analytics-gradebook'],
    queryFn: () => analyticsApi.getTenantAnalytics(),
    staleTime: 60000,
  });

  const tenantStats = tenantRes?.data?.data as {
    total_exams?: number;
    total_attempts?: number;
    total_students?: number;
    active_exams?: number;
    average_pass_rate?: number;
  } | null;

  const { data: examsRes } = useQuery({
    queryKey: ['exams-for-gradebook'],
    queryFn: () => examsApi.list({ page: 1, per_page: 200 }),
    staleTime: 30000,
  });

  const exams: Exam[] = Array.isArray(examsRes?.data?.data?.data)
    ? examsRes.data.data.data
    : Array.isArray(examsRes?.data?.data)
      ? examsRes.data.data
      : [];

  const filteredExams = examSearch
    ? exams.filter((e) => e.title.toLowerCase().includes(examSearch.toLowerCase()))
    : exams;

  const selectedExamTitle = exams.find((e) => e.id === selectedExamId)?.title || '';

  const { data: gradesRes, isLoading: gradesLoading } = useQuery({
    queryKey: ['gradebook', selectedExamId, page],
    queryFn: () => gradebookApi.getExamGrades(selectedExamId, { page, per_page: 20 }),
    enabled: !!selectedExamId,
    staleTime: 30000,
  });

  const grades: GradeEntry[] = Array.isArray(gradesRes?.data?.data) ? (gradesRes.data.data as GradeEntry[]) : [];
  const totalPages = gradesRes?.data?.meta?.total_pages || 1;

  const { data: statsRes } = useQuery({
    queryKey: ['grade-stats', selectedExamId],
    queryFn: () => gradebookApi.getGradeStats(selectedExamId),
    enabled: !!selectedExamId,
    staleTime: 30000,
  });

  const stats: GradeStats = (statsRes?.data?.data || {}) as GradeStats;

  const updateGradeMutation = useMutation({
    mutationFn: ({ attemptId, data }: { attemptId: string; data: { score: number; feedback?: string } }) =>
      gradebookApi.updateGrade(attemptId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradebook'] });
      queryClient.invalidateQueries({ queryKey: ['grade-stats'] });
      toast.success('Grade updated');
      setEditingId(null);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Failed to update grade'),
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: (data: { grades: Array<{ attempt_id: string; score: number; feedback?: string }> }) =>
      gradebookApi.bulkUpdateGrades(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gradebook'] });
      queryClient.invalidateQueries({ queryKey: ['grade-stats'] });
      toast.success('Grades updated in bulk');
      setBulkModalOpen(false);
      setSelectedIds([]);
      setBulkScore(0);
      setBulkFeedback('');
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Bulk update failed'),
  });

  const recalcMutation = useMutation({
    mutationFn: () => gradebookApi.recalculateGrades(selectedExamId),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['gradebook'] });
      queryClient.invalidateQueries({ queryKey: ['grade-stats'] });
      const count = (res?.data as any)?.data?.recalculated || 0;
      toast.success(`${count} grades recalculated`);
      setConfirmRecalc(false);
    },
    onError: (error: any) => toast.error(error?.response?.data?.message || 'Recalculation failed'),
  });

  const handleExport = async () => {
    try {
      const response = await gradebookApi.exportGrades(selectedExamId);
      const blob = new Blob([response.data as any], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `grades-${selectedExamId}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Grades exported');
    } catch {
      toast.error('Export failed');
    }
  };

  const startEdit = (entry: GradeEntry) => {
    setEditingId(entry.attempt_id);
    setEditScore(entry.score);
    setEditFeedback(entry.feedback || '');
  };

  const saveEdit = () => {
    if (!editingId) return;
    updateGradeMutation.mutate({ attemptId: editingId, data: { score: editScore, feedback: editFeedback } });
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === grades.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(grades.map((g) => g.attempt_id));
    }
  };

  const handleBulkUpdate = () => {
    bulkUpdateMutation.mutate({
      grades: selectedIds.map((id) => ({ attempt_id: id, score: bulkScore, feedback: bulkFeedback || undefined })),
    });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Gradebook</h1>
          <p className="text-sm text-white/50 mt-1">View and manage exam grades</p>
        </div>
      </div>

      {/* Overall Stats - always visible */}
      {tenantStats && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          <StatCard
            title="Total Exams"
            value={tenantStats.total_exams ?? 0}
            icon={<BookOpen className="w-4 h-4" />}
            color="teal"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Attempts"
            value={tenantStats.total_attempts ?? 0}
            icon={<BarChart3 className="w-4 h-4" />}
            color="blue"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Total Students"
            value={tenantStats.total_students ?? 0}
            icon={<Users className="w-4 h-4" />}
            color="purple"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Active Exams"
            value={tenantStats.active_exams ?? 0}
            icon={<Activity className="w-4 h-4" />}
            color="green"
            className="!p-3 [&_h3]:!text-xl"
          />
          <StatCard
            title="Avg Pass Rate"
            value={`${(tenantStats.average_pass_rate ?? 0).toFixed(1)}%`}
            icon={<TrendingUp className="w-4 h-4" />}
            color="orange"
            className="!p-3 [&_h3]:!text-xl"
          />
        </div>
      )}

      {/* Exam selector with search */}
      <div className="card-dark p-4">
        <label className="block text-sm font-medium text-white/70 mb-2">Select Exam</label>
        <div className="relative w-full max-w-md">
          <div
            className="input-dark w-full flex items-center cursor-pointer"
            onClick={() => setExamDropdownOpen(!examDropdownOpen)}
          >
            <Search className="w-4 h-4 text-white/40 mr-2 flex-shrink-0" />
            {examDropdownOpen ? (
              <input
                type="text"
                value={examSearch}
                onChange={(e) => setExamSearch(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search exams..."
                className="bg-transparent border-none outline-none text-white/90 w-full text-sm placeholder:text-white/40"
                autoFocus
              />
            ) : (
              <span className={`text-sm truncate ${selectedExamId ? 'text-white/90' : 'text-white/40'}`}>
                {selectedExamTitle || 'Search and select an exam...'}
              </span>
            )}
            {selectedExamId && !examDropdownOpen && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedExamId('');
                  setExamSearch('');
                  setPage(1);
                  setSelectedIds([]);
                }}
                className="ml-auto text-white/40 hover:text-white/70"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {examDropdownOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => { setExamDropdownOpen(false); setExamSearch(''); }} />
              <div className="absolute z-20 mt-1 w-full bg-[#1a1f2e] border border-white/[0.1] rounded-lg shadow-xl max-h-64 overflow-y-auto">
                {filteredExams.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-white/40">No exams found</div>
                ) : (
                  filteredExams.map((exam) => (
                    <button
                      key={exam.id}
                      onClick={() => {
                        setSelectedExamId(exam.id);
                        setExamDropdownOpen(false);
                        setExamSearch('');
                        setPage(1);
                        setSelectedIds([]);
                      }}
                      className={`w-full text-left px-4 py-2.5 text-sm hover:bg-white/[0.06] transition-colors flex items-center justify-between ${
                        selectedExamId === exam.id ? 'bg-white/[0.04] text-teal-400' : 'text-white/80'
                      }`}
                    >
                      <span className="truncate">{exam.title}</span>
                      <span className="ml-2 text-xs text-white/30 flex-shrink-0">{exam.status}</span>
                    </button>
                  ))
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {selectedExamId && (
        <>
          {/* Stats bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              title="Average Score"
              value={stats.average_score?.toFixed(1) || '0'}
              icon={<BarChart3 className="w-5 h-5" />}
              color="teal"
              subtitle={`Lowest: ${stats.lowest_score?.toFixed(1) || '0'}`}
            />
            <StatCard
              title="Highest Score"
              value={stats.highest_score?.toFixed(1) || '0'}
              icon={<TrendingUp className="w-5 h-5" />}
              color="blue"
              subtitle={`Std Dev: ${stats.std_deviation?.toFixed(1) || '0'}`}
            />
            <StatCard
              title="Pass Rate"
              value={stats.pass_rate != null ? `${stats.pass_rate.toFixed(1)}%` : '0%'}
              icon={<Award className="w-5 h-5" />}
              color="green"
              subtitle={`${stats.graded_count || 0} graded`}
            />
            <StatCard
              title="Total Attempts"
              value={stats.total_attempts || 0}
              icon={<Users className="w-5 h-5" />}
              color="purple"
            />
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-3">
            {selectedIds.length > 0 && (
              <button onClick={() => setBulkModalOpen(true)} className="btn-primary flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
                <CheckSquare className="w-4 h-4" />
                <span>Bulk Grade ({selectedIds.length})</span>
              </button>
            )}
            <button onClick={handleExport} className="btn-outline flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
              <Download className="w-4 h-4" />
              <span>Export</span>
            </button>
            <button onClick={() => setConfirmRecalc(true)} className="btn-outline flex items-center space-x-2 px-4 py-2 rounded-lg text-sm">
              <RefreshCw className="w-4 h-4" />
              <span>Recalculate</span>
            </button>
          </div>

          {/* Grades table */}
          <div className="card-dark overflow-hidden">
            {gradesLoading ? (
              <div className="p-8 text-center text-white/50">Loading grades...</div>
            ) : grades.length === 0 ? (
              <div className="p-8 text-center text-white/50">No grades found for this exam</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/[0.06]">
                      <th className="p-3 text-left">
                        <input type="checkbox" checked={selectedIds.length === grades.length && grades.length > 0} onChange={toggleSelectAll} className="rounded" />
                      </th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Student</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Score</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Percentage</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Grade</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Status</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Feedback</th>
                      <th className="p-3 text-left text-xs font-medium text-white/50 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grades.map((entry) => (
                      <tr key={entry.attempt_id} className="border-b border-white/[0.04] hover:bg-white/[0.02]">
                        <td className="p-3">
                          <input type="checkbox" checked={selectedIds.includes(entry.attempt_id)} onChange={() => toggleSelect(entry.attempt_id)} className="rounded" />
                        </td>
                        <td className="p-3">
                          <p className="text-sm font-medium text-white/90">{entry.student_name}</p>
                          <p className="text-xs text-white/50">{entry.student_email}</p>
                        </td>
                        <td className="p-3">
                          {editingId === entry.attempt_id ? (
                            <input
                              type="number"
                              value={editScore}
                              onChange={(e) => setEditScore(Number(e.target.value))}
                              className="input-dark w-20 text-sm"
                            />
                          ) : (
                            <span className="text-sm text-white/90">
                              {entry.score}/{entry.total_marks}
                              {entry.plagiarism_penalty != null && entry.plagiarism_penalty > 0 && (
                                <span className="ml-1 text-xs text-rose-400" title={`Plagiarism penalty: -${entry.plagiarism_penalty}%`}>
                                  (-{entry.plagiarism_penalty}%)
                                </span>
                              )}
                            </span>
                          )}
                        </td>
                        <td className="p-3 text-sm text-white/90">{entry.percentage?.toFixed(1)}%</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            entry.grade.startsWith('A') ? 'bg-emerald-500/20 text-emerald-400' :
                            entry.grade.startsWith('B') ? 'bg-teal-500/20 text-teal-400' :
                            entry.grade.startsWith('C') ? 'bg-amber-500/20 text-amber-400' :
                            entry.grade === 'D' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-red-500/20 text-red-400'
                          }`}>{entry.grade || '—'}</span>
                        </td>
                        <td className="p-3">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${entry.passed ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                            {entry.passed ? 'Passed' : 'Failed'}
                          </span>
                        </td>
                        <td className="p-3">
                          {editingId === entry.attempt_id ? (
                            <input
                              type="text"
                              value={editFeedback}
                              onChange={(e) => setEditFeedback(e.target.value)}
                              className="input-dark w-40 text-sm"
                              placeholder="Feedback..."
                            />
                          ) : (
                            <span className="text-sm text-white/60 truncate max-w-[200px] block">{entry.feedback || '—'}</span>
                          )}
                        </td>
                        <td className="p-3">
                          <div className="flex items-center space-x-2">
                            {editingId === entry.attempt_id ? (
                              <>
                                <button onClick={saveEdit} className="text-emerald-400 hover:text-emerald-300" title="Save"><Save className="w-4 h-4" /></button>
                                <button onClick={() => setEditingId(null)} className="text-red-400 hover:text-red-300" title="Cancel"><X className="w-4 h-4" /></button>
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => navigate(`/teacher/gradebook/${entry.attempt_id}?exam=${selectedExamId}&name=${encodeURIComponent(entry.student_name)}&email=${encodeURIComponent(entry.student_email)}`)}
                                  className="text-teal-400 hover:text-teal-300"
                                  title="View Details"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button onClick={() => startEdit(entry)} className="text-white/50 hover:text-white/90" title="Edit Grade"><Edit3 className="w-4 h-4" /></button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 p-4 border-t border-white/[0.06]">
                <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-outline px-3 py-1 text-sm rounded disabled:opacity-50">
                  Previous
                </button>
                <span className="text-sm text-white/60">Page {page} of {totalPages}</span>
                <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-outline px-3 py-1 text-sm rounded disabled:opacity-50">
                  Next
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Bulk Grade Modal */}
      {bulkModalOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setBulkModalOpen(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white/90 mb-4">Bulk Grade ({selectedIds.length} students)</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-white/70 mb-1">Score</label>
                <input type="number" value={bulkScore} onChange={(e) => setBulkScore(Number(e.target.value))} className="input-dark w-full" />
              </div>
              <div>
                <label className="block text-sm text-white/70 mb-1">Feedback (optional)</label>
                <textarea value={bulkFeedback} onChange={(e) => setBulkFeedback(e.target.value)} className="input-dark w-full" rows={3} />
              </div>
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button onClick={() => setBulkModalOpen(false)} className="btn-outline px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={handleBulkUpdate} disabled={bulkUpdateMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm">
                {bulkUpdateMutation.isPending ? 'Updating...' : 'Update All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recalculate Confirmation Modal */}
      {confirmRecalc && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center" onClick={() => setConfirmRecalc(false)}>
          <div className="bg-[#1a1f2e] border border-white/[0.08] rounded-xl p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white/90 mb-2">Recalculate Grades?</h3>
            <p className="text-sm text-white/60 mb-6">This will recalculate all grades for this exam based on current scoring rules.</p>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setConfirmRecalc(false)} className="btn-outline px-4 py-2 rounded-lg text-sm">Cancel</button>
              <button onClick={() => recalcMutation.mutate()} disabled={recalcMutation.isPending} className="btn-primary px-4 py-2 rounded-lg text-sm">
                {recalcMutation.isPending ? 'Recalculating...' : 'Recalculate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
